import { Button } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { Resource } from 'utoniq-ui'

import {
  FavoriteMemberImageUploadCell,
  FavoriteMemberNameInputCell,
} from '@/components/organisms/console'
import { useTranslation } from '@/core'
import { CellFrame } from '@/core/architecture'
import { Form, tLabel, tMessage, useForm, UseFormProps } from '@/core/form'

export type FavoriteMemberEditFormValues = {
  memberName: string
  avatarPath: string | null
}

export type Props = {
  onSubmit: (values: { avatarPath: string; name: string }) => Promise<void>
  onDrop: () => Promise<Resource | null>
  progress: number
  isSubmitting?: boolean
  favoriteMember?: {
    favoriteMemberId: string
    name: string
    avatar: string
  }
}

const schema: UseFormProps['schema'] = o => ({
  memberName: o
    .string()
    .max(50)
    .custom((value, helpers) => {
      // 特殊文字チェック
      const match = value.match(/[=+\-@\t\r\n;,]+/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel('consoleFavoriteMemberEdit.memberName'),
          }),
        })
      }
      return value
    })
    .label(tLabel('consoleFavoriteMemberEdit.memberName')),
  avatarPath: o.string().allow(null),
})

export const FavoriteMemberEditForm: React.FC<Props> = ({
  onSubmit,
  onDrop,
  progress,
  isSubmitting = false,
  favoriteMember,
}) => {
  const { t } = useTranslation('consoleFavoriteMemberEdit')
  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FavoriteMemberEditFormValues>({
    schema,
    defaultValues: {
      memberName: '',
      avatarPath: null,
    },
  })

  // 初期値のアバター画像
  const defaultAvatarResource = useMemo(() => {
    return {
      id: '',
      resourceName: '',
      imageUrl: favoriteMember?.avatar,
      imageUploadPath: favoriteMember?.avatar,
    }
  }, [favoriteMember])

  // 初期値が変更されたときにフォームをリセット
  useEffect(() => {
    reset({
      memberName: favoriteMember?.name,
      avatarPath: favoriteMember?.avatar,
    })
  }, [favoriteMember?.avatar, favoriteMember?.name])

  const handleOnSubmit = handleSubmit(
    (values: FavoriteMemberEditFormValues) => {
      return onSubmit({
        name: values.memberName,
        avatarPath: values.avatarPath || '',
      })
    }
  )

  return (
    <Form
      id="favorite-member-edit-form"
      onSubmit={handleOnSubmit}
      sx={{ px: 5 }}
    >
      {/* 画像アップロードエリア */}
      <FavoriteMemberImageUploadCell
        onDrop={onDrop}
        setValue={setValue}
        onDelete={() => {
          setValue('avatarPath', null, { shouldValidate: true })
        }}
        progress={progress}
        defaultResource={defaultAvatarResource}
        fieldName="avatarPath"
        translationNamespace="consoleFavoriteMemberEdit"
      />
      {/* メンバー名入力エリア */}
      <FavoriteMemberNameInputCell
        value={watch('memberName')}
        onChange={(value: string) => {
          setValue('memberName', value, {
            shouldValidate: true,
          })
        }}
        error={errors?.memberName}
        translationNamespace="consoleFavoriteMemberEdit"
      />

      {/* 更新ボタン */}
      <CellFrame sx={{ py: 5, textAlign: 'center' }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          sx={{ width: 244 }}
          disabled={
            isSubmitting || !watch('avatarPath') || !watch('memberName').trim()
          }
          loading={isSubmitting}
        >
          {t('register')}
        </Button>
      </CellFrame>
    </Form>
  )
}
